<script setup>
import { useMenu } from "../../composables/useMenu.js";

const props = defineProps({
  pageNumber: { type: Number, required: true },
  pageHidden: { type: Boolean, default: false },
});
const emit = defineEmits(["rotate", "toggle-visibility"]);

const { isOpen, triggerEl, menuEl, menuStyle, toggle, close, onMenuKeydown } = useMenu();

const itemClass =
  "flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md bg-transparent text-left text-[13px] text-slate-700 cursor-pointer " +
  "enabled:hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed " +
  "dark:text-slate-300 dark:enabled:hover:bg-slate-700";

function handleRotate(delta) {
  // Rotating a hidden page is disabled — it isn't shown in the viewer, so the
  // user couldn't see the result. Unhide it first.
  if (props.pageHidden) return;
  close();
  emit("rotate", props.pageNumber, delta);
}

function handleToggleVisibility() {
  close();
  emit("toggle-visibility", props.pageNumber);
}
</script>

<template>
  <button
    ref="triggerEl"
    type="button"
    class="w-full h-full flex items-center justify-center p-0 rounded-full bg-white text-slate-500 cursor-pointer
      shadow-[0_2px_8px_rgba(0,0,0,0.18)] transition-colors duration-[120ms] hover:bg-slate-100 hover:text-slate-700
      dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 dark:hover:text-slate-100"
    title="Page options"
    aria-haspopup="menu"
    :aria-expanded="isOpen"
    @click.stop="toggle"
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  </button>

  <Teleport to="body">
    <div
      v-if="isOpen"
      ref="menuEl"
      role="menu"
      aria-label="Page options"
      :style="menuStyle"
      class="min-w-[140px] p-1 z-[400] bg-white border border-slate-200 rounded-[10px] shadow-[0_4px_16px_rgba(0,0,0,0.12)]
        dark:bg-slate-800 dark:border-slate-700 dark:shadow-[0_4px_16px_rgba(0,0,0,0.4)]"
      data-thumbnail-rail-popover
      @keydown="onMenuKeydown"
      @click.stop
    >
      <button
        role="menuitem"
        :class="itemClass"
        :disabled="pageHidden"
        :title="pageHidden ? 'Show the page to rotate it' : undefined"
        @click="handleRotate(-90)"
      >
        <svg class="-scale-x-100" width="14" height="14" viewBox="0 0 20 20" fill="none">
          <path
            d="M12.0167 1.66675L10.895 2.59175C10.2983 3.08425 10 3.33008 10 3.63675H10.8333C13.9758 3.63675 15.5475 3.63675 16.5233 4.61258C17.4992 5.58841 17.5 7.16008 17.5 10.3034V10.8334M2.5 12.5001C2.5 15.2501 2.5 16.6251 3.35417 17.4792C4.20833 18.3334 5.58333 18.3334 8.33333 18.3334C11.0833 18.3334 12.4583 18.3334 13.3125 17.4792C14.1667 16.6251 14.1667 15.2501 14.1667 12.5001C14.1667 9.75008 14.1667 8.37508 13.3125 7.52091C12.4583 6.66675 11.0833 6.66675 8.33333 6.66675C5.58333 6.66675 4.20833 6.66675 3.35417 7.52091C2.5 8.37508 2.5 9.75008 2.5 12.5001Z"
            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
          />
        </svg>
        <span>Rotate left</span>
      </button>
      <button
        role="menuitem"
        :class="itemClass"
        :disabled="pageHidden"
        :title="pageHidden ? 'Show the page to rotate it' : undefined"
        @click="handleRotate(90)"
      >
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
          <path
            d="M12.0167 1.66675L10.895 2.59175C10.2983 3.08425 10 3.33008 10 3.63675H10.8333C13.9758 3.63675 15.5475 3.63675 16.5233 4.61258C17.4992 5.58841 17.5 7.16008 17.5 10.3034V10.8334M2.5 12.5001C2.5 15.2501 2.5 16.6251 3.35417 17.4792C4.20833 18.3334 5.58333 18.3334 8.33333 18.3334C11.0833 18.3334 12.4583 18.3334 13.3125 17.4792C14.1667 16.6251 14.1667 15.2501 14.1667 12.5001C14.1667 9.75008 14.1667 8.37508 13.3125 7.52091C12.4583 6.66675 11.0833 6.66675 8.33333 6.66675C5.58333 6.66675 4.20833 6.66675 3.35417 7.52091C2.5 8.37508 2.5 9.75008 2.5 12.5001Z"
            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
          />
        </svg>
        <span>Rotate right</span>
      </button>
      <button role="menuitem" :class="itemClass" @click="handleToggleVisibility">
        <svg v-if="!pageHidden" width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z"
            stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"
          />
          <circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.4" />
        </svg>
        <svg v-else width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z"
            stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"
          />
          <circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.4" />
          <path d="M2 2l12 12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
        </svg>
        <span>{{ pageHidden ? "Unhide page" : "Hide page" }}</span>
      </button>
    </div>
  </Teleport>
</template>
